import React from 'react';

import cs from 'classnames';

import {PaginatedList} from 'app/general/widgets/PaginatedList';
import {gt} from 'app/utils/i18n';

import {BaseListWidget} from './BaseListWidget';

import type {UseListQueryType} from 'app/store/sweet-api/api-query';
import type {ListResponseType} from 'sweet-types';

interface DashboardListWidgetProps<P, T> {
  icon: string;
  title: string;
  hook: UseListQueryType<P, T>;
  params: P;
  emptyMessage?: string;
  renderItem: (item: T, index: number, isLast: boolean) => React.ReactNode;
}

export function DashboardListWidget<P, T extends {url: string}>({
  icon,
  title,
  hook,
  params,
  emptyMessage = gt('Nothing to show'),
  renderItem,
}: DashboardListWidgetProps<P, T>) {
  return (
    <BaseListWidget title={title} icon={icon}>
      <PaginatedList
        hook={hook}
        params={params}
        render={(data) => (
          <ItemList
            data={data}
            renderItem={renderItem}
            emptyMessage={emptyMessage}
          />
        )}
      />
    </BaseListWidget>
  );
}

interface ItemListProps<T> {
  data?: ListResponseType<T>;
  renderItem: (item: T, index: number, isLast: boolean) => React.ReactNode;
  emptyMessage?: string;
}
function ItemList<T extends {url: string}>({
  data,
  renderItem,
  emptyMessage,
}: ItemListProps<T>) {
  const hasData = data && data.count > 0;

  return hasData ? (
    <ul className="list-unstyled mb-0">
      {data.results.map((item, index) => {
        const isLast = index === data.results.length - 1;
        return (
          <li key={item.url} className={cs({'mb-3': !isLast})}>
            {renderItem(item, index, isLast)}
          </li>
        );
      })}
    </ul>
  ) : (
    <span>{emptyMessage}</span>
  );
}
